import { all, put, takeLatest, call } from 'redux-saga/effects';
import { EmailsActionTypes } from 'types';
import {
  listReferralsEmailsSuccess,
  listReferralsEmailsFailure,
  listResponsesEmailsFailure,
  listResponsesEmailsSuccess,
  getReferralEmailDetailFailure,
  getReferralEmailDetailSuccess,
  getResponsesEmailDetailFailure,
  getResponsesEmailDetailSuccess,
  previewEmailSuccess,
  previewEmailFailure,
  resendEmailSuccess,
  resendEmailFailure,
  resendToOtherEmailSuccess,
  resendToOtherEmailFailure,
  downloadEmailBodyFailure,
  downloadEmailBodySuccess
} from './actions';
import {
  fetchEmailWithDetail,
  resendEmails,
  resendToOtherEmails
} from 'services/emails.service';
import {
  fetchReferralsEmailsService,
  fetchResponsesService,
  fetchReferralByIdService
} from 'services/referral.service';
import { omit, sortBy } from 'lodash';
import { fetchResponseWithDetail } from 'services/enquiry.service';

function* handleListReferralsEmails(request: any) {
  try {
    const response = yield fetchReferralsEmailsService(request.payload);
    if (response.error) {
      return yield put(listReferralsEmailsFailure(response.error.message));
    }
    const OutboundEmails =
      response.Referrals?.map((r: any) => {
        const emails = r.OutboundEmails[0];
        const Referral = omit(r, ['OutboundEmails']);
        return {
          ...emails,
          createdAtReferral: r.createdAt,
          Referral
        };
      }) || [];
    yield put(
      listReferralsEmailsSuccess({
        Info: response.Info,
        OutboundEmails
      })
    );
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(listReferralsEmailsFailure(errMsg));
  }
}

function* handleListResponsesEmails(request: any) {
  try {
    const response = yield fetchResponsesService(request.payload);
    if (response.error) {
      return yield put(listResponsesEmailsFailure(response.error.message));
    }
    const OutboundEmails =
      response.Responses?.map((r: any) => {
        const emails = r.OutboundEmails[0];
        return {
          ...emails,
          createdAtResponse: r.createdAt,
          Referral: r.Referral,
          Response: { id: r.id, inboundEmailId: r.inboundEmailId }
        };
      }) || [];
    yield put(
      listResponsesEmailsSuccess({
        Info: response.Info,
        OutboundEmails
      })
    );
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(listResponsesEmailsFailure(errMsg));
  }
}

function* handleGetReferralEmail(request: any) {
  try {
    const { emailId } = request.payload;
    const response = yield fetchEmailWithDetail({
      direction: 'outbound',
      emailId
    });
    if (response.error) {
      return yield put(getReferralEmailDetailFailure(response.error.message));
    }

    const Events = sortBy(response.Events, ['createdAt']);
    yield put(
      getReferralEmailDetailSuccess({
        ...response,
        Events
      })
    );
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(getReferralEmailDetailFailure(errMsg));
  }
}

function* handleGetResponseEmail(request: any) {
  try {
    const { enquiryId, referralId, emailId, responseId } = request.payload;
    const promises = yield Promise.all([
      fetchEmailWithDetail({
        direction: 'outbound',
        emailId
      }),
      fetchReferralByIdService(enquiryId, referralId, true)
    ]);
    const email = promises[0];
    const responses = promises[1];
    if (email.error || responses.error) {
      return yield put(
        getResponsesEmailDetailFailure(
          email.error.message || responses.error.message
        )
      );
    }
    const response = responses.Responses.find((r: any) => r.id === responseId);
    const Events = sortBy(email.Events, ['createdAt']);
    yield put(
      getResponsesEmailDetailSuccess({
        ...email,
        Events,
        Response: response
      })
    );
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(getResponsesEmailDetailFailure(errMsg));
  }
}

function* handlePreviewEmail(request: any) {
  try {
    const { emailId } = request.payload;
    const response = yield fetchEmailWithDetail({
      direction: 'outbound',
      emailId
    });
    if (response.error) {
      return yield put(previewEmailFailure(response.error.message));
    }
    yield put(previewEmailSuccess(response));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(previewEmailFailure(errMsg));
  }
}

function* handlePreviewResponse(request: any) {
  try {
    const { responseId, shareId = '', enquiryId = '' } = request.payload;
    const response = yield fetchResponseWithDetail({
      responseId,
      shareId,
      enquiryId
    });
    if (response.error) {
      return yield put(previewEmailFailure(response.error.message));
    }
    yield put(previewEmailSuccess(response));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(previewEmailFailure(errMsg));
  }
}

function* handleResendEmail(request: any) {
  try {
    const { emailId } = request.payload;
    const response = yield resendEmails({ emailId });
    if (response.error) {
      return yield put(resendEmailFailure(response.error.message));
    }
    yield put(resendEmailSuccess(response));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(resendEmailFailure(errMsg));
  }
}

function* handleResendToOtherEmail(request: any) {
  try {
    const { emailId, emailAddress } = request.payload;
    const response = yield resendToOtherEmails({ emailId, emailAddress });
    if (response.error) {
      return yield put(resendToOtherEmailFailure(response.error.message));
    }
    yield put(resendToOtherEmailSuccess(response));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(resendToOtherEmailFailure(errMsg));
  }
}

function* handleDownloadEmailBody(request: any) {
  try {
    const {
      emailId,
      direction,
      downloadEmail = false,
      shareId
    } = request.payload;
    const response = yield fetchEmailWithDetail({
      direction,
      emailId,
      downloadEmail,
      shareId
    });
    if (response.error) {
      return yield put(downloadEmailBodyFailure(response.error.message));
    }
    yield put(downloadEmailBodySuccess(response));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(downloadEmailBodyFailure(errMsg));
  }
}

function* watchListReferralsEmails() {
  yield takeLatest(
    EmailsActionTypes.LIST_REFERRALS_EMAILS_START,
    handleListReferralsEmails
  );
}

function* watchListResponsesEmails() {
  yield takeLatest(
    EmailsActionTypes.LIST_RESPONSES_EMAILS_START,
    handleListResponsesEmails
  );
}

function* watchGetReferralEmail() {
  yield takeLatest(
    EmailsActionTypes.GET_REFERRAL_EMAIL_DETAIL_START,
    handleGetReferralEmail
  );
}

function* watchGetResponseEmail() {
  yield takeLatest(
    EmailsActionTypes.GET_RESPONSE_EMAIL_DETAIL_START,
    handleGetResponseEmail
  );
}

function* watchPreviewEmail() {
  yield takeLatest(EmailsActionTypes.PREVIEW_EMAIL_START, handlePreviewEmail);
}

function* watchPreviewResponse() {
  yield takeLatest(
    EmailsActionTypes.PREVIEW_RESPONSE_START,
    handlePreviewResponse
  );
}

function* watchResendEmail() {
  yield takeLatest(EmailsActionTypes.RESEND_EMAIL_START, handleResendEmail);
}

function* watchResendToOtherEmail() {
  yield takeLatest(
    EmailsActionTypes.RESEND_TO_OTHER_EMAIL_START,
    handleResendToOtherEmail
  );
}

function* watchDownloadEmailBody() {
  yield takeLatest(
    EmailsActionTypes.DOWNLOAD_EMAIL_BODY_START,
    handleDownloadEmailBody
  );
}

function* emailSagas() {
  yield all([
    call(watchListReferralsEmails),
    call(watchListResponsesEmails),
    call(watchGetReferralEmail),
    call(watchGetResponseEmail),
    call(watchPreviewEmail),
    call(watchPreviewResponse),
    call(watchResendEmail),
    call(watchResendToOtherEmail),
    call(watchDownloadEmailBody)
  ]);
}

export default emailSagas;
